import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { LogIn, Search, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatLastUpdated = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const MemberManagement = ({ members, token }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const handleLoginAsShopper = async (member) => {
    try {
      const response = await axios.post(`${API}/admin/auth/login-as`, {
        email: member.email,
        user_type: "shopper",
      }, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem("bizkeep_token", response.data.access_token);
      localStorage.setItem("bizkeep_email", response.data.email);
      localStorage.setItem("bizkeep_user_type", "shopper");
      toast.success(`Logged in as ${member.name || member.email}`);
      window.open("/shopper-profile", "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to login as shopper");
    }
  };

  const filteredMembers = members.filter((member) => {
    const search = searchTerm.toLowerCase();
    return !searchTerm ||
      member.name?.toLowerCase().includes(search) ||
      member.email?.toLowerCase().includes(search);
  });

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{members.length}</p>
              <p className="text-sm text-slate-500">Members</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Manage Members</h1>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-10 border-slate-200"
            data-testid="admin-member-search"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {filteredMembers.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            {searchTerm ? "No members found matching your search." : "No members yet."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Saved Businesses</TableHead>
                <TableHead className="font-semibold">Followed Markets</TableHead>
                <TableHead className="font-semibold">Last Updated</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.map((member) => (
                <TableRow key={member.id || member.email} className="hover:bg-slate-50">
                  <TableCell className="font-medium">{member.name || "Unnamed"}</TableCell>
                  <TableCell className="text-slate-600">{member.email}</TableCell>
                  <TableCell className="text-slate-600">{member.saved_business_ids?.length || 0}</TableCell>
                  <TableCell className="text-slate-600">{member.saved_market_ids?.length || 0}</TableCell>
                  <TableCell className="text-slate-600">{formatLastUpdated(member.updated_at || member.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleLoginAsShopper(member)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Login as shopper"
                      data-testid={`admin-login-as-shopper-${member.id || member.email}`}
                    >
                      <LogIn className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
